/**
 * CommandPalette — global Cmd/Ctrl+K search.
 *
 * Searches across the three things the topbar's placeholder promised:
 * devices (cross-vendor inventory), sites, and workflow runs. Each store
 * is already loaded into Zustand by the pages that need it; this palette
 * triggers a load on open so a user who opens it before visiting any of
 * those pages still gets results.
 *
 * Match logic is intentionally simple — case-insensitive substring across
 * the most useful fields per type. No external search dep. Top N per
 * type so the list stays readable; if a user types something specific
 * enough they'll find what they want without ranking magic.
 *
 * Hits Enter or clicks a result → navigates + closes the palette.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Server, Building2, PlayCircle, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { normalizeHotelName } from "@/lib/hotel-name";
import { useInventory } from "@/stores/inventory";
import { useSites } from "@/stores/sites";
import { useWorkflowRuns } from "@/stores/workflow-runs";

const MAX_PER_GROUP = 6;

interface Result {
  /** Stable id used by React + keyboard nav. */
  id: string;
  /** Bucket label for the section header. */
  group: "Devices" | "Sites" | "Workflow runs";
  /** Primary line — bolded in the row. */
  label: string;
  /** Subtitle line — dimmer + smaller. */
  hint: string;
  /** Route to navigate to when the row is activated. */
  to: string;
  /** Icon for the row. */
  icon: React.ReactNode;
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Pull each store's data. We don't gate on status — even an idle store
  // returns an empty array which is fine (the empty-state copy below
  // covers it). Loads are kicked off in the open-effect right after.
  const devices = useInventory((s) => s.data);
  const sites = useSites((s) => s.sites);
  const runs = useWorkflowRuns((s) => s.data);
  const loadDevices = useInventory((s) => s.load);
  const loadSites = useSites((s) => s.loadSites);
  const loadRuns = useWorkflowRuns((s) => s.load);

  // On open, kick a refresh + focus the input. Stores guard against
  // concurrent loads so this is cheap if data is already warm.
  useEffect(() => {
    if (!open) return;
    void loadDevices();
    void loadSites();
    void loadRuns();
    // Defer focus until after Radix has mounted the dialog content.
    const t = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [open, loadDevices, loadSites, loadRuns]);

  const results = useMemo<Result[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const out: Result[] = [];
    // --- Devices: match name / serial / model / IP / site ---
    let dHits = 0;
    for (const d of devices) {
      if (dHits >= MAX_PER_GROUP) break;
      const text =
        `${d.name ?? ""} ${d.serial} ${d.model ?? ""} ${d.ipAddress ?? ""} ${d.mac ?? ""} ${d.site ?? ""}`.toLowerCase();
      if (!text.includes(q)) continue;
      out.push({
        id: `device:${d.vendor}:${d.serial}`,
        group: "Devices",
        label: d.name || d.serial,
        hint: `${d.vendor} · ${d.model ?? "—"} · ${d.site ?? "no site"}`,
        to: "/devices",
        icon: <Server size={14} />,
      });
      dHits += 1;
    }
    // --- Sites: match code / friendlyName / org / navision ---
    let sHits = 0;
    for (const s of sites) {
      if (sHits >= MAX_PER_GROUP) break;
      const text =
        `${s.code} ${s.friendlyName} ${s.org} ${s.navisionCode ?? ""}`.toLowerCase();
      if (!text.includes(q)) continue;
      const code = (s.innCode || s.code).toUpperCase();
      const name = normalizeHotelName(s.friendlyName || s.code);
      out.push({
        id: `site:${s.slug}`,
        group: "Sites",
        label: `${name} - ${code}`,
        hint: `${s.navisionCode ? `Nav: ${s.navisionCode} · ` : ""}${s.org}`,
        to: `/sites/${s.slug}`,
        icon: <Building2 size={14} />,
      });
      sHits += 1;
    }
    // --- Workflow runs: match workflow name / target / triggered-by ---
    let rHits = 0;
    for (const r of runs) {
      if (rHits >= MAX_PER_GROUP) break;
      const text =
        `${r.workflowName} ${r.workflowSlug} ${r.target} ${r.triggeredBy} ${r.message ?? ""}`.toLowerCase();
      if (!text.includes(q)) continue;
      out.push({
        id: `run:${r.id}`,
        group: "Workflow runs",
        label: r.workflowName,
        hint: `${r.target} · ${r.state} · ${r.triggeredBy}`,
        to: "/workflows",
        icon: <PlayCircle size={14} />,
      });
      rHits += 1;
    }
    return out;
  }, [query, devices, sites, runs]);

  // Keyboard nav — Enter activates the first result. Up/Down to move
  // selection would be nice but for v1 Enter-takes-first is enough to
  // keep the interaction fast.
  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && results.length > 0) {
      e.preventDefault();
      activate(results[0]!);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onOpenChange(false);
    }
  }

  function activate(r: Result) {
    navigate(r.to);
    onOpenChange(false);
  }

  // Group results for display. Map preserves insertion order so the
  // section ordering matches the search order above (devices, sites,
  // runs).
  const grouped = useMemo(() => {
    const m = new Map<Result["group"], Result[]>();
    for (const r of results) {
      const list = m.get(r.group) ?? [];
      list.push(r);
      m.set(r.group, list);
    }
    return [...m.entries()];
  }, [results]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl gap-0 overflow-hidden p-0">
        <DialogTitle className="sr-only">
          Search devices, sites, and workflows
        </DialogTitle>
        <div className="flex items-center gap-2.5 border-b border-line px-4 py-3">
          <Search size={16} className="text-fg-mute" />
          <input
            ref={inputRef}
            aria-label="Search devices, sites, workflows"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKey}
            placeholder="Search devices, sites, workflows…"
            className="flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-mute"
            spellCheck={false}
            autoComplete="off"
          />
          <span className="rounded border border-line bg-surface-tint px-1.5 py-0.5 font-mono text-[10px] text-fg-mute">
            Esc
          </span>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {query.trim() === "" ? (
            <div className="px-3 py-8 text-center text-xs text-fg-mute">
              Type to search across devices, sites, and recent workflow runs.
            </div>
          ) : results.length === 0 ? (
            <div className="px-3 py-8 text-center text-xs text-fg-mute">
              No matches. The palette only sees what's already loaded (devices /
              sites / workflows) — visit those pages once if you just signed in.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {grouped.map(([group, list]) => (
                <div key={group} className="flex flex-col gap-0.5">
                  <div className="px-2 pb-1 pt-1 text-[10px] font-mono uppercase tracking-[0.12em] text-fg-mute">
                    {group}
                  </div>
                  {list.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => activate(r)}
                      className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-aurora-soft"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-surface-2 text-violet">
                        {r.icon}
                      </span>
                      <span className="flex flex-1 flex-col gap-0.5 overflow-hidden">
                        <span className="truncate text-sm font-medium text-fg">
                          {r.label}
                        </span>
                        <span className="truncate text-xs text-fg-dim">
                          {r.hint}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between border-t border-line px-4 py-2 text-[11px] text-fg-mute">
          <span>
            <kbd className="rounded border border-line bg-surface-tint px-1 py-0.5 font-mono text-[10px]">
              ↵
            </kbd>{" "}
            opens the first result
          </span>
          <span className="inline-flex items-center gap-1">
            <X size={11} />
            Close
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
